'use client';

import { event } from '@/app/lib/analytics/ga';
import { simpleRenderToString } from '@/app/lib/utils/jsx';
import { generateReactContent } from '@/app/lib/utils/jsx-preview-utils';
import { formatHTML, kebabCase } from '@/app/lib/utils/string';
import { clsxMerge } from '@/app/lib/utils/styles';
import CheckIcon from '@/app/ui/assets/icons/check-icon';
import CopyIcon from '@/app/ui/assets/icons/copy-icon';
import MonitorIcon from '@/app/ui/assets/icons/monitor-icon';
import SmartphoneIcon from '@/app/ui/assets/icons/smartphone-icon';
import RequireJSLabel from '@/app/ui/components/molecules/component-preview/require-js-label';
import { Button } from '@/app/ui/components/atoms/button';
import CodepenEdit from '@/app/ui/components/molecules/component-preview/codepen-edit';
import { Preview } from '@/app/ui/components/molecules/component-preview/preview';
import Highlighter from '@/app/ui/components/molecules/highlighter';
import type { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { SegmentedControl } from '@/app/ui/components/atoms/segmented-control';
import { ToastProvider } from 'tailframes-components/components/toast';

type Mode = 'preview' | 'html' | 'react';

export interface ReactPreviewAdditionalComponentProps {
  name: string;
  content: string;
  defaultComponent?: boolean;
  props?: Array<string | { name: string; replacer: string | string[] }>;
  raw?: boolean;
}

interface ReactPreviewProps {
  content: string;
  additionalComponents?: ReactPreviewAdditionalComponentProps[];
}

export interface ComponentPreviewProps extends PropsWithChildren {
  label: string;
  gap?: 0 | 6 | 12 | 16;
  direction?: 'row' | 'column';
  className?: string;
  mobilePreviewClassName?: string;
  react: ReactPreviewProps | string;
  eagerLoading?: boolean;
  js?: string;
  htmlComponent?: ReactNode;
  hideCodePen?: boolean;
  disableHtml?: boolean;
  disableReact?: boolean;
}

export default function ComponentPreview({
  children,
  react,
  label,
  gap = 6,
  direction = 'row',
  className,
  mobilePreviewClassName,
  eagerLoading = false,
  js,
  htmlComponent,
  hideCodePen = false,
  disableHtml = false,
  disableReact = false,
}: ComponentPreviewProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [mode, setMode] = useState<Mode>('preview');
  const [isCopied, setIsCopied] = useState(false);

  const CopyButtonIcon = isCopied ? CheckIcon : CopyIcon;

  const headerId = kebabCase(label);
  const isCodeMode = mode !== 'preview';

  // TODO semantic tags replacement in HTML & React content eg. h3 -> h1, h4 -> h2 etc.
  const [htmlContent, setHtmlContent] = useState('');
  const [htmlContentWithJs, setHtmlContentWithJs] = useState('');
  const [reactContent, setReactContent] = useState('');

  const handleModeChange = useCallback((mode: Mode) => {
    setMode(mode);

    event({
      action: 'component_preview_mode_change',
      category: 'user_interaction',
      label: mode,
    });
  }, []);

  const handleCopyContent = useCallback(() => {
    if (mode === 'html') {
      void navigator.clipboard.writeText(htmlContentWithJs);
    } else if (mode === 'react') {
      void navigator.clipboard.writeText(reactContent);
    }

    setIsCopied(true);

    event({
      action: 'component_preview_code_copy',
      category: 'user_interaction',
      label: mode,
    });
  }, [mode, htmlContentWithJs, reactContent]);

  const handleHtmlContent = useCallback(async () => {
    if (!htmlContentWithJs) {
      const html = await simpleRenderToString((htmlComponent ?? children) as unknown as ReactElement);
      const formattedHtml = formatHTML(html);
      setHtmlContent(formattedHtml);
      setHtmlContentWithJs(js ? `${formattedHtml}\n\n<!-- Required JavaScript -->\n${formatHTML(js)}` : formattedHtml);
    }
  }, [htmlComponent, js, htmlContentWithJs, children]);

  const handleReactContent = useCallback(async () => {
    if (!reactContent) {
      setReactContent(generateReactContent(react));
    }
  }, [reactContent, react]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsCopied(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
    };
  }, [isCopied]);

  useEffect(() => {
    setIsCopied(false);
  }, [mode]);

  useEffect(() => {
    void (mode === 'html' && handleHtmlContent());
  }, [mode, handleHtmlContent]);

  useEffect(() => {
    void (mode === 'react' && handleReactContent());
  }, [mode, handleReactContent]);

  useEffect(() => {
    // Preparing React & HTML previews content
    const timeoutId = setTimeout(
      () => {
        void handleHtmlContent();
        void handleReactContent();
      },
      eagerLoading ? 0 : 2000
    );

    return () => {
      clearTimeout(timeoutId);
    };
  }, [eagerLoading, handleHtmlContent, handleReactContent]);

  return (
    <div className='w-full' data-testid='component-preview'>
      <div className='mb-4 flex grow flex-col items-center justify-between md:flex-row'>
        <div className='group mb-4 flex w-full items-center gap-4 md:mb-0 md:w-auto'>
          <div>
            <div id={headerId} className='invisible -mt-4 pt-4 xl:-mt-24 xl:pt-24' />
            <h2 className='text-sm font-semibold'>{label}</h2>
          </div>
          {js && <RequireJSLabel />}
          <a
            className='-ml-1 font-semibold text-blue-700 opacity-0 transition-opacity duration-300 group-hover:opacity-100'
            href={`#${headerId}`}
            aria-label={`Link to the section: ${label}`}
          >
            #
          </a>
        </div>
        <div className='flex w-full items-center gap-4 md:w-auto'>
          {!hideCodePen && (
            <CodepenEdit
              title={label}
              content={`<div class='${clsxMerge(
                `flex w-full flex-col items-center justify-center px-6 py-12 gap-${gap} md:flex-${direction}`,
                className,
                isMobile ? mobilePreviewClassName : ''
              )}'>${htmlContent}</div>`}
              disabled={!htmlContent}
              js={js}
            />
          )}
          <SegmentedControl
            className='hidden md:inline-flex'
            value={isMobile ? 'mobile' : 'desktop'}
            onChange={preview => {
              setIsMobile(preview === 'mobile');
              setMode('preview');
            }}
            items={[
              {
                value: 'desktop',
                label: <MonitorIcon stroke='currentColor' />,
                tooltip: {
                  value: 'Desktop preview',
                },
              },
              {
                value: 'mobile',
                label: <SmartphoneIcon stroke='currentColor' />,
                tooltip: {
                  value: 'Mobile Preview',
                },
              },
            ]}
          />
          <SegmentedControl
            value={mode}
            onChange={value => {
              handleModeChange(value as Mode);
            }}
            items={[
              {
                value: 'preview',
                label: 'Preview',
              },
              {
                value: 'html',
                label: 'HTML',
                disabled: disableHtml,
              },
              {
                value: 'react',
                label: 'React',
                disabled: disableReact,
              },
            ]}
          />
          <Button
            className={`hidden pl-0 hover:text-inherit md:inline-flex`}
            variant='text-default'
            size='small'
            onClick={handleCopyContent}
            startAdornment={<CopyButtonIcon className='stroke-inherit' />}
            disabled={!isCodeMode}
          >
            {isCopied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </div>
      <div className={`transition-all duration-200 ease-in ${!isCodeMode ? 'opacity-100' : 'invisible h-0 opacity-0'}`}>
        <ToastProvider>
          <Preview isMobile={isMobile} gap={gap} direction={direction} className={className} mobilePreviewClassName={mobilePreviewClassName}>
            {children}
          </Preview>
        </ToastProvider>
      </div>
      <div className={`transition-all duration-200 ease-in ${isCodeMode ? 'opacity-100' : 'opacity-0'}`}>
        {mode === 'html' && <Highlighter language='html' content={htmlContentWithJs} />}
        {mode === 'react' && <Highlighter language='jsx' content={reactContent} />}
      </div>
    </div>
  );
}
